import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { FeedbackRecord } from '../services/learningApi';
import { useApis } from './learning/common';
import { RelativeTime } from './RelativeTime';

interface Props {
  sourceType: 'function_run' | 'chain_execution';
  sourceId: string;
  /** Admins viewing another account's run: that account. */
  customerId?: string | null;
  /** Change to reload (e.g. after the thumbs buttons save). */
  refreshKey?: number;
  sx?: SxProps<Theme>;
}

const MAX_VALUE_CHARS = 120;

function scoreColor(score: number): 'success' | 'error' | 'default' {
  if (score > 0) return 'success';
  if (score < 0) return 'error';
  return 'default';
}

function shortValue(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text;
}

/** Every feedback signal recorded on a run (thumbs, API labels such as accepted / rejected / click). Hidden when there is none. */
export const RunFeedbackList: React.FC<Props> = ({ sourceType, sourceId, customerId, refreshKey, sx }) => {
  const apis = useApis();
  const [items, setItems] = useState<FeedbackRecord[]>([]);

  useEffect(() => {
    if (!sourceId) return;
    let cancelled = false;
    apis.feedback
      .list(sourceType, sourceId, customerId)
      .then((r) => !cancelled && setItems(r.feedback))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [apis, sourceType, sourceId, customerId, refreshKey]);

  if (!items.length) return null;

  return (
    <Card sx={sx ?? { mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Feedback on this run ({items.length})
        </Typography>
        <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {items.map((f) => {
            const meta = Object.entries(f.metadata || {}).filter(([, v]) => v !== null && v !== '');
            return (
              <Box key={f.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title={`Score ${f.score > 0 ? '+' : ''}${f.score.toFixed(1)} (-1 bad … +1 great)`}>
                    <Chip size="small" label={f.label.replace(/_/g, ' ')} color={scoreColor(f.score)} />
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary">
                    {f.via_api ? 'Sent through the API' : f.created_by || 'Portal'}
                  </Typography>
                  <RelativeTime value={f.created_at} />
                </Box>
                {f.item_ref && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>About: </Box>
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>{f.item_ref}</Box>
                  </Typography>
                )}
                {f.note && (
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                    “{f.note}”
                  </Typography>
                )}
                {meta.length > 0 && (
                  <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 }}>
                    {meta.map(([k, v]) => (
                      <Typography key={k} variant="caption" color="text.secondary">
                        {k}: <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>{shortValue(v)}</Box>
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RunFeedbackList;
